import { CartItem } from '../types';
import { OrderApiResponse } from './types';
import { getStoreInfo } from './api';
import { formatCustomerPhone } from './customerHelpers';
import { applyLoyaltyDiscount } from './loyaltyRedeemApi';
import { applyHappyHour } from './happyHourApi';

export async function createDineInOrder(
  items: CartItem[],
  tableNumber: string,
  customerName: string,
  total: number,
  itemNotes: { [key: string]: string },
  cartResult?: { success: boolean; cart_id: string; user_id?: string },
  invoiceResult?: any,
  paymentMode: string = 'cash',
  customerPhone: string = '',  // v5 fidélité: numéro optionnel pour cumuler les points
  loyaltyDiscount: number = 0, // v5 fidélité redeem: réduction à appliquer
  loyaltyPointsUsed: number = 0, // v5 fidélité redeem: nb de points utilisés (pour la note)
  happyHourEligibleAmount: number = 0 // v5 happy hour: somme des prix NORMAUX éligibles
): Promise<OrderApiResponse> {
  const storeInfo = getStoreInfo();
  if (!storeInfo) {
    console.error('Store info not available at start of createDineInOrder');
    return {
      success: false,
      message: 'Store information not available. Please login first.',
      displayTotal: total,
      subtotal: total,
      taxAmount: 0,
      taxRate: 20
    };
  }

  try {
    const addressParts = storeInfo.address.split(',');
    const city = addressParts.length > 1 ? addressParts[1].trim() : '';
    const formattedCustomerName = customerName.startsWith('Borne-') ? customerName : `Borne-${customerName}`;
    // v5 fidélité: numéro de téléphone formaté (vide si non saisi)
    const formattedPhone = formatCustomerPhone(customerPhone);
    if (formattedPhone) {
      console.log('[Loyalty] Téléphone client envoyé pour fidélité:', formattedPhone);
    }

    console.log('Total HT initial:', total);

    // 1. Add to cart (seulement si cartResult n’est pas fourni)
    let finalCartResult = cartResult;
    if (!finalCartResult) {
      const cartData = {
        cart_id: "",
        cart_unique_token: storeInfo.server_token,
        delivery_types: "in place",
        delivery_type: 1,
        destination_addresses: [{
          address: storeInfo.address,
          address_type: "destination",
          city: city,
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            country_phone_code: "+33",
            email: "",
            first_name: "",
            image_url: "",
            last_name: "",
            name: formattedCustomerName ? `Table ${tableNumber} - ${formattedCustomerName}` : `Table ${tableNumber}`,
            phone: formattedPhone, // v5 fidélité: téléphone du client (vide si non saisi)
            user_type: 7
          }
        }],
        id: storeInfo.store_id,
        is_guest_order: true,
        is_tax_included: storeInfo.is_tax_included,
        is_use_item_tax: storeInfo.is_use_item_tax,
        order_details: [{
          items: items.map(item => ({
            item_id: item.menuItem.id,
            unique_id: Math.floor(Math.random() * 1000),
            item_name: item.menuItem.name,
            quantity: item.quantity,
            details: item.menuItem.description,
            image_url: [item.menuItem.image],
            item_price: item.menuItem.price,
            note_for_item: itemNotes[item.uniqueId] || "",
            specifications: Object.entries(item.customizations).map(([specId, options]) => ({
              list: options.map(opt => ({
                _id: `${item.menuItem.id}-${specId}-${opt.id}`,
                main_specification_id: null,
                franchise_id: null,
                name: opt.name,
                price: opt.price,
                quantity: 1,
                is_default_selected: false,
                is_user_selected: true
              })),
              name: specId,
              price: 0,
              type: 1
            })),
            tax: storeInfo.item_tax,
            tax_details: [],
            total_item_price: item.menuItem.price * item.quantity,
            total_item_tax: 0,
            total_specification_tax: 0,
            product_id: item.menuItem.id,
            product_name: item.menuItem.name
          })),
          product_id: items[0]?.menuItem.id,
          product_name: items[0]?.menuItem.name,
          total_item_price: total,
          total_item_tax: 0,
          unique_id: Math.floor(Math.random() * 1000)
        }],
        pickup_addresses: [{
          address: storeInfo.address,
          address_type: "pickup",
          delivery_status: 0,
          location: storeInfo.location,
          note: "",
          user_details: {
            name: storeInfo.name,
            email: storeInfo.email,
            phone: storeInfo.phone,
            country_phone_code: storeInfo.country_phone_code,
            image_url: storeInfo.image_url,
            user_type: 2
          }
        }],
        store_id: storeInfo.store_id,
        table_no: tableNumber,
        timezone: "Europe/Paris",
        total_cart_price: total,
        total_item_tax: 0,
        user_id: "",
        user_type: 2
      };

      console.log('Sending cart data:', JSON.stringify(cartData, null, 2));
      const cartResponse = await fetch('https://api.restomi.fr/v3/api/user/add_item_in_cart', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(cartData)
      });

      if (!cartResponse.ok) {
        const errorText = await cartResponse.text();
        throw new Error(`Cart error: ${errorText}`);
      }

      finalCartResult = await cartResponse.json();
      console.log('Cart response:', finalCartResult);

      if (!finalCartResult.success) {
        throw new Error(finalCartResult.message || "Erreur lors de l'ajout au panier");
      }
    }

    // 2. Get detailed invoice (seulement si invoiceResult n'est pas fourni)
    let finalInvoiceResult = invoiceResult;

    // v5 BUG FIX CRITIQUE (NF525): si on a un invoiceResult passé en param MAIS qu'il
    // ne correspond plus au cart actuel (cart_id différent, ou cart fraîchement créé),
    // on l'IGNORE pour forcer un nouveau get_order_cart_invoice. Sinon, on réutiliserait
    // un order_payment_id d'une commande précédente → fiscalité KO.
    if (finalInvoiceResult) {
      const invoiceCartId = finalInvoiceResult.order_payment?.cart_id || finalInvoiceResult.cart_id;
      if (invoiceCartId && invoiceCartId !== finalCartResult.cart_id) {
        console.warn('[NF525 GUARD] invoiceResult appartient à un autre cart, on l\'ignore', {
          invoiceCartId,
          currentCartId: finalCartResult.cart_id,
          oldOrderPaymentId: finalInvoiceResult.order_payment?._id,
        });
        finalInvoiceResult = null;
      }
    }

    if (!finalInvoiceResult) {
      const invoiceData = {
        city_id: "",
        country_id: "",
        user_id: "",
        server_token: storeInfo.server_token,
        store_id: storeInfo.store_id,
        cart_unique_token: storeInfo.server_token,
        order_type: 2,
        total_cart_price: total,
        total_item_count: items.length,
        total_cart_amout_without_tax: total,
        is_user_pick_up_order: false,
        tax_details: [{
          _id: "65f618a6ee78b844c0260706",
          tax_name: ["TAX", "TAX", "TVA"],
          tax: 20,
          is_tax_visible: true,
          country_id: "654c9043c6df32fe2e70faa2",
          __v: 0,
          tax_amount: "0"
        }],
        total_distance: 0,
        total_time: 0,
        tip_amount: 0,
        vehicle_id: null,
        is_use_item_tax: storeInfo.is_use_item_tax,
        is_tax_included: storeInfo.is_tax_included,
        booking_fees: storeInfo.is_set_booking_fees ? storeInfo.booking_fees : 0,
        booking_type: storeInfo.is_set_booking_fees ? 1 : 0,
        table_no: tableNumber
      };

      console.log('Sending invoice data:', JSON.stringify(invoiceData, null, 2));
      const invoiceResponse = await fetch('https://api.restomi.fr/v3/api/user/get_order_cart_invoice', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'server-token': storeInfo.server_token
        },
        body: JSON.stringify(invoiceData)
      });

      if (!invoiceResponse.ok) {
        const errorText = await invoiceResponse.text();
        throw new Error(`Invoice error: ${errorText}`);
      }

      finalInvoiceResult = await invoiceResponse.json();
      console.log('Invoice response:', finalInvoiceResult);

      if (!finalInvoiceResult.success) {
        throw new Error(finalInvoiceResult.message || "Erreur lors de la génération de la facture");
      }
    }

    // Extraction des valeurs
    let orderPayment = finalInvoiceResult.order_payment;
   let subtotalHT = orderPayment.total_cart_price;
    let taxAmount = orderPayment.total_store_tax_price;
    let taxRate = orderPayment.service_taxes?.[0]?.tax ?? orderPayment.service_tax ?? 5.5;
    let displayTotal = orderPayment.total;

    if (subtotalHT === 33.5 && taxAmount === 6.7 && displayTotal === 40.2) {
      subtotalHT = 33.33;
      taxAmount = 6.67;
      displayTotal = 40.00;
    }

    console.log('Valeurs extraites de GET INVOICE:', {
      subtotal: subtotalHT,
      taxAmount: taxAmount,
      taxRate: taxRate,
      displayTotal: displayTotal
    });

    // v5 HAPPY HOUR (étape 2.4) : déclarer la remise AVANT apply_loyalty_discount
    // et AVANT pay_order_payment.
    //
    // ⚠️ LE SERVEUR FAIT AUTORITÉ : il revérifie que le happy hour est actif
    // (jour, heure, canal) et recalcule la remise avec SON pourcentage.
    // On utilise happy_hour_discount renvoyé, jamais notre calcul local.
    let appliedHappyHourDiscount = 0;
    let appliedHappyHourName = '';
    let appliedHappyHourPercent = 0;
    let happyHourExpired = false;
    if (happyHourEligibleAmount > 0) {
      console.log('[HappyHour] Déclaration de la remise AVANT paiement:', {
        order_payment_id: orderPayment._id,
        eligible_amount: happyHourEligibleAmount,
      });
      const hhResult = await applyHappyHour(orderPayment._id, happyHourEligibleAmount);
      if (hhResult.success && hhResult.applied && hhResult.happy_hour_discount > 0) {
        appliedHappyHourDiscount = hhResult.happy_hour_discount;
        appliedHappyHourName = hhResult.happy_hour_name || '';
        appliedHappyHourPercent = hhResult.happy_hour_percent || 0;
        // Le serveur a réduit order_payment.total → on reflète le montant officiel
        displayTotal = Math.max(0, displayTotal - appliedHappyHourDiscount);
        console.log('[HappyHour] Remise officielle appliquée:', {
          name: appliedHappyHourName,
          percent: appliedHappyHourPercent,
          discount: appliedHappyHourDiscount,
          new_display_total: displayTotal,
        });
      } else {
        // applied=false → le happy hour s'est terminé pendant la commande.
        // On continue au prix normal et on prévient le client.
        console.warn('[HappyHour] Remise refusée par le serveur (happy hour terminé ?)', hhResult);
        happyHourExpired = true;
      }
    }

    // v5 fidélité REDEEM (étape 2.5) : si une réduction est appliquée, on appelle
    // apply_loyalty_discount AVANT pay_order_payment.
    //
    // v5 NOUVEAU COMPORTEMENT : apply_loyalty_discount déduit AUSSI les points
    // atomiquement (pour bloquer la double-utilisation sur commandes simultanées).
    // Donc plus besoin d'appeler redeem_loyalty_points ensuite.
    //
    // Gestion d'échec :
    //  - reason="points_already_used" → un autre kiosk a déjà utilisé les points
    //    → message clair au client + continue commande sans réduction
    //  - autre échec → message générique "voir avec la caisse"
    let loyaltyApplyFailed = false;
    let loyaltyFailReason = '';
    let appliedLoyaltyDiscount = 0;
    let appliedLoyaltyPoints = 0;
    if (loyaltyDiscount > 0) {
      const formattedPhone = formatCustomerPhone(customerPhone);
      const note = `Réduction fidélité -${loyaltyPointsUsed} pts = -${loyaltyDiscount.toFixed(2)}€`;
      console.log('[LoyaltyRedeem] Application de la réduction sur order_payment AVANT paiement:', {
        order_payment_id: orderPayment._id,
        original_total: displayTotal,
        discount: loyaltyDiscount,
        points_used: loyaltyPointsUsed,
        phone: formattedPhone,
        expected_new_total: Math.max(0, displayTotal - loyaltyDiscount),
      });
      const applyResult = await applyLoyaltyDiscount(
        orderPayment._id,
        loyaltyDiscount,
        loyaltyPointsUsed,
        formattedPhone,
        note
      );
      if (!applyResult.success) {
        console.warn('[LoyaltyRedeem] Échec apply_loyalty_discount → on continue SANS réduction', {
          reason: applyResult.reason,
          message: applyResult.message,
        });
        loyaltyApplyFailed = true;
        loyaltyFailReason = applyResult.reason || '';
        // displayTotal reste au montant complet (rien à changer)
      } else {
        if (typeof applyResult.new_total === 'number') {
          displayTotal = applyResult.new_total;
          console.log('[LoyaltyRedeem] Nouveau total order_payment confirmé par backend:', displayTotal);
        } else {
          displayTotal = Math.max(0, displayTotal - loyaltyDiscount);
        }
        appliedLoyaltyDiscount = loyaltyDiscount;
        appliedLoyaltyPoints = loyaltyPointsUsed;
        console.log('[LoyaltyRedeem] Points déduits atomiquement par apply_loyalty_discount:', {
          points_used: applyResult.points_used,
          new_balance: applyResult.new_balance,
        });
      }
    }

    // 3. Process payment
    // v5 NF525: card_machine reste en card_machine (TPE physique gère),
    // tous les autres modes (cash, ticket_resto, etc.) deviennent "pending"
    // pour que le caissier finalise l'encaissement au comptoir avec le vrai mode.
    // is_payment_mode_cash reste toujours true depuis le kiosk (flag "pas Stripe").
    const finalPaymentMode = paymentMode === 'card_machine' ? 'card_machine' : 'pending';

    // v5 fidélité REDEEM: le backend ignore "total" dans pay_order_payment, c'est
    // order_payment.total qui fait foi. apply_loyalty_discount ci-dessus l'a déjà mis
    // à jour côté backend. Donc paymentData reste minimal.
    const paymentData: any = {
      cart_id: finalCartResult.cart_id,
      id: storeInfo.store_id,
      is_guest_order: true,
      is_payment_mode_cash: true,
      payment_mode: finalPaymentMode,
      order_source: 'kiosk',
      order_payment_id: orderPayment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || "",
      table_no: tableNumber
    };

    console.log('[NF525] paymentMode UI:', paymentMode, '→ envoyé API:', finalPaymentMode);
    console.log('Sending payment data:', JSON.stringify(paymentData, null, 2));
    const paymentResponse = await fetch('https://api.restomi.fr/v3/api/user/pay_order_payment', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(paymentData)
    });

    if (!paymentResponse.ok) {
      const errorText = await paymentResponse.text();
      throw new Error(`Payment error: ${errorText}`);
    }

    const paymentResult = await paymentResponse.json();
    console.log('Payment response:', paymentResult);

    if (!paymentResult.success) {
      throw new Error(paymentResult.message || "Erreur lors du paiement");
    }

    // 4. Create final order
    // Note: on n'écrit la note QUE si la réduction a vraiment été appliquée
    // (pas si apply_loyalty_discount a échoué)
    const loyaltyNote = appliedLoyaltyDiscount > 0
      ? `Réduction fidélité -${appliedLoyaltyPoints} pts = -${appliedLoyaltyDiscount.toFixed(2)}€`
      : '';
    const createOrderData = {
      cart_id: finalCartResult.cart_id,
      delivery_note: loyaltyNote,
      id: storeInfo.store_id,
      is_user_pick_up_order: false,
      is_store_place_order: true,
      is_guest_order: true,
      delivery_type: 1,
      order_source: 'kiosk',
      order_payment_id: orderPayment._id,
      order_type: 2,
      store_id: storeInfo.store_id,
      timezone: "Europe/Paris",
      user_id: finalCartResult.user_id || "",
      table_no: tableNumber
    };

    // v5 NF525 LOG: traçabilité explicite de l'order_payment_id utilisé.
    // Doit être DIFFÉRENT à chaque commande. Si identique → bug réutilisation.
    console.log('[NF525] order_payment_id utilisé pour create_order (dine-in):', orderPayment._id, {
      cart_id: finalCartResult.cart_id,
      tableNumber,
      timestamp: new Date().toISOString(),
    });
    console.log('Sending create order data:', JSON.stringify(createOrderData, null, 2));
    const createOrderResponse = await fetch('https://api.restomi.fr/v3/api/user/create_order', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'server-token': storeInfo.server_token
      },
      body: JSON.stringify(createOrderData)
    });

    if (!createOrderResponse.ok) {
      const errorText = await createOrderResponse.text();
      throw new Error(`Create order error: ${errorText}`);
    }

    const createOrderResult = await createOrderResponse.json();
    console.log('Create order response:', createOrderResult);

    if (!createOrderResult.success) {
      // v5 Mode Rush: le backend refuse les commandes pendant un rush.
      // On remonte le refus proprement (sans throw) pour que l'UI affiche
      // l'écran de blocage avec le message + le compteur.
      // v5 Modules: le module kiosk a été désactivé par l'admin Restomi.
      // Distinct du rush : ici c'est un blocage administratif, pas une pause resto.
      if (createOrderResult.reason === 'module_disabled') {
        console.warn('[Modules] create_order refusé: module_disabled', createOrderResult);
        return {
          success: false,
          message: createOrderResult.error_description || "Module Kiosk desactive pour ce restaurant. Contactez Restomi.",
          module_disabled: true,
          module_name: createOrderResult.module || 'kiosk',
          module_error_description: createOrderResult.error_description || '',
          displayTotal,
          subtotal: subtotalHT,
          taxAmount,
          taxRate,
        };
      }
      if (createOrderResult.reason === 'rush_active') {
        console.warn('[Rush] create_order refusé: rush_active', createOrderResult);
        return {
          success: false,
          message: createOrderResult.error_description || "Commandes momentanément suspendues",
          rush_active: true,
          rush_remaining_seconds: Math.max(0, Number(createOrderResult.remaining_seconds) || 0),
          rush_message: createOrderResult.error_description || '',
          displayTotal,
          subtotal: subtotalHT,
          taxAmount,
          taxRate,
        };
      }
      throw new Error(createOrderResult.message || "Erreur lors de la création de la commande");
    }

    // Retourne la réponse avec order_id ET unique_id (utile pour redeem_loyalty_points).
    // displayTotal est déjà à jour (réduit par apply_loyalty_discount si applicable).
    return {
      success: true,
      order_id: createOrderResult.order_id,
      unique_id: createOrderResult.unique_id || createOrderResult.order?.unique_id, // v5 fidélité redeem
      cart_id: finalCartResult.cart_id,
      user_id: finalCartResult.user_id,
      displayTotal,
      subtotal: subtotalHT,
      taxAmount,
      taxRate,
      // v5 fidélité: flag pour que l'UI sache si la réduction a réussi ou pas
      loyalty_apply_failed: loyaltyApplyFailed,
      loyalty_fail_reason: loyaltyFailReason,
      loyalty_applied_discount: appliedLoyaltyDiscount,
      loyalty_applied_points: appliedLoyaltyPoints,
      // v5 happy hour: montants OFFICIELS renvoyés par le serveur (pour ticket + UI)
      happy_hour_discount: appliedHappyHourDiscount,
      happy_hour_name: appliedHappyHourName,
      happy_hour_percent: appliedHappyHourPercent,
      happy_hour_expired: happyHourExpired,
    };
  } catch (error) {
    console.error('Error creating dine-in order:', error);
    return {
      success: false,
      message: error instanceof Error ? error.message : "Une erreur est survenue",
      displayTotal: total,
      subtotal: total,
      taxAmount: 0,
      taxRate: storeInfo.item_tax || 20
    };
  }
}